import { useEffect, type RefObject } from "react";

export function useLandingMotion(root: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const page = root.current;
    if (!page || !("IntersectionObserver" in window)) return;
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    let observer: IntersectionObserver | undefined;
    const elements = Array.from(
      page.querySelectorAll<HTMLElement>("[data-reveal]"),
    );
    function show(element: HTMLElement) {
      element.removeAttribute("data-reveal-pending");
      observer?.unobserve(element);
    }
    function configure() {
      observer?.disconnect();
      elements.forEach((element) =>
        element.removeAttribute("data-reveal-pending"),
      );
      if (preference.matches) return;
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries)
            if (entry.isIntersecting) show(entry.target as HTMLElement);
        },
        { threshold: 0.08, rootMargin: "0px 0px -24px 0px" },
      );
      for (const element of elements) {
        if (element.getBoundingClientRect().top < window.innerHeight - 24)
          continue;
        element.setAttribute("data-reveal-pending", "");
        observer.observe(element);
      }
    }
    function focus(event: FocusEvent) {
      const target =
        event.target instanceof Element
          ? event.target.closest<HTMLElement>("[data-reveal]")
          : null;
      if (target) show(target);
    }
    configure();
    preference.addEventListener("change", configure);
    page.addEventListener("focusin", focus);
    return () => {
      observer?.disconnect();
      elements.forEach((element) =>
        element.removeAttribute("data-reveal-pending"),
      );
      preference.removeEventListener("change", configure);
      page.removeEventListener("focusin", focus);
    };
  }, [root]);
}
